/**
 * Tetris easter egg — modal overlay with a classic Tetris board.
 * Always mounted (never unmounted) for the session lifetime; hidden via CSS when closed.
 * Closing the panel pauses the game. Reopening shows "Reprendre" / "Rejouer".
 *
 * Controls: ↑ rotate, ← → move, ↓ soft drop, Space hard drop, P pause/resume, R restart.
 * Score is saved to /users/tetris-score on pause, logout, restart, and window unload.
 */

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useTetris } from '../hooks/useTetris';
import type { NextPiece } from '../hooks/useTetris';
import { TETRIS_CONFIG } from '../constants/TetrisConfig';
import styles from './TetrisPanel.module.css';

interface Props {
  /** Controls visibility. Component stays mounted when false (state preserved). */
  isOpen: boolean;
  /** Called to hide the panel (does not unmount). */
  onClose: () => void;
}

const { boardWidth: W, boardHeight: H } = TETRIS_CONFIG;

// ── Sizing constants ──────────────────────────────────────────────────────────

const SIDEBAR_W = 160;   // stats column width (px)
const BOARD_GAP = 22;    // gap between board and stats (px)
const PANEL_PAD = 20;    // modal padding each side (px)
const VP_MARGIN = 40;    // viewport margin each side (px)
const CTRL_H = 140;      // controls area height incl. top margin (px)
const MIN_CELL = 14;
const MAX_CELL = 32;

/** Recomputes cellSize from the current viewport. */
function computeCellSize(): number {
  const fitH = (window.innerHeight - 2 * VP_MARGIN - CTRL_H - 2 * PANEL_PAD) / H;
  const fitW = (window.innerWidth  - 2 * VP_MARGIN - SIDEBAR_W - BOARD_GAP - 2 * PANEL_PAD) / W;
  return Math.max(MIN_CELL, Math.min(MAX_CELL, Math.floor(Math.min(fitH, fitW))));
}

// ── Sub-components ────────────────────────────────────────────────────────────

/** Renders a Tetris grid (board or next-piece preview). */
function TetrisGrid({
  cells,
  cols,
  rows,
  cellPx,
}: {
  cells: (string | null)[][];
  cols: number;
  rows: number;
  cellPx: number;
}) {
  return (
    <div
      className={styles.grid}
      style={{
        gridTemplateColumns: `repeat(${cols}, ${cellPx}px)`,
        width: cols * cellPx,
        height: rows * cellPx,
      }}
    >
      {cells.map((row, r) =>
        row.map((color, c) => (
          <div
            key={`${r}-${c}`}
            className={styles.cell}
            style={color ? { backgroundColor: color } : undefined}
            data-filled={color ? '' : undefined}
          />
        )),
      )}
    </div>
  );
}

/** Renders the next-piece preview in a 4×4 grid (padded to center). */
function NextPiecePreview({ piece }: { piece: NextPiece }) {
  const size = 4;
  const grid: (string | null)[][] = Array.from({ length: size }, () =>
    Array(size).fill(null),
  );
  const offsetR = Math.floor((size - piece.shape.length) / 2);
  const offsetC = Math.floor((size - piece.shape[0].length) / 2);
  for (let r = 0; r < piece.shape.length; r++) {
    for (let c = 0; c < piece.shape[r].length; c++) {
      if (piece.shape[r][c]) {
        grid[offsetR + r][offsetC + c] = piece.color;
      }
    }
  }
  return <TetrisGrid cells={grid} cols={size} rows={size} cellPx={14} />;
}

/** Displays a score value as spaced digit cells. */
function ScoreDisplay({ value }: { value: number }) {
  const digits = String(value).split('').reverse();
  while (digits.length < 7) digits.push(' ');
  digits.reverse();
  return (
    <div className={styles.scoreDigits}>
      {digits.map((d, i) => (
        <span key={i} className={styles.digit}>{d}</span>
      ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Full Tetris game modal. Always mounted; visible only when isOpen is true.
 * @param isOpen  - show/hide the overlay (game state is preserved when false)
 * @param onClose - called to hide the panel (does NOT unmount the component)
 */
export default function TetrisPanel({ isOpen, onClose }: Props) {
  const {
    board,
    next,
    score,
    lines,
    level,
    status,
    maxScore,
    start,
    pause,
    resume,
    moveLeft,
    moveRight,
    softDrop,
    rotate,
    hardDrop,
    setMaxScore,
  } = useTetris();

  const overlayRef = useRef<HTMLDivElement>(null);

  // Refs to avoid stale closures in event handlers registered once
  const statusRef = useRef(status);
  statusRef.current = status;
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // Keep latest score values for save calls
  const latestRef = useRef({ score, lines, level });
  latestRef.current = { score, lines, level };

  // Dynamic cell size
  const [cellSize, setCellSize] = useState(computeCellSize);

  // ── Lifecycle ───────────────────────────────────────────────────────────────

  // Responsive cell size
  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout>;
    const debounced = () => {
      clearTimeout(timerId);
      timerId = setTimeout(() => setCellSize(computeCellSize()), 100);
    };
    window.addEventListener('resize', debounced);
    return () => { window.removeEventListener('resize', debounced); clearTimeout(timerId); };
  }, []);

  // Fetch persisted highscore once on mount — POST with score 0 never updates the DB
  useEffect(() => {
    axios
      .post<{ highscore: number }>('/users/tetris-score', { score: 0, lines: 0, level: 1 })
      .then((r) => setMaxScore(r.data.highscore))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save score on unmount (logout path)
  useEffect(() => () => saveScore(), []); // eslint-disable-line react-hooks/exhaustive-deps

  // Save score on page unload
  useEffect(() => {
    window.addEventListener('beforeunload', saveScore);
    return () => window.removeEventListener('beforeunload', saveScore);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Score save ──────────────────────────────────────────────────────────────

  /** Fire-and-forget score upload. Errors are swallowed. */
  const saveScore = () => {
    const { score: s, lines: l, level: lv } = latestRef.current;
    axios.post('/users/tetris-score', { score: s, lines: l, level: lv }).catch(() => {});
  };

  // ── Game actions ────────────────────────────────────────────────────────────

  /** Pause + save score. No-op if not playing. */
  const handlePause = () => {
    if (statusRef.current === 'playing') {
      saveScore();
      pause();
    }
  };

  /** Save score then start a fresh game. */
  const handleRestart = () => {
    saveScore();
    start();
  };

  // ── Event handlers ──────────────────────────────────────────────────────────

  // Keyboard controls — registered once; use refs to read current status/isOpen
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!isOpenRef.current) return;
      const key = e.key;

      if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' '].includes(key)) {
        e.stopPropagation();
        e.preventDefault();
        if (statusRef.current === 'playing') {
          if (key === 'ArrowLeft') moveLeft();
          else if (key === 'ArrowRight') moveRight();
          else if (key === 'ArrowDown') softDrop();
          else if (key === 'ArrowUp') rotate();
          else if (key === ' ') hardDrop();
        }
      }

      if (key === 'p' || key === 'P') {
        if (statusRef.current === 'playing') { saveScore(); pause(); }
        else if (statusRef.current === 'paused') resume();
      }

      if (key === 'r' || key === 'R') {
        if (statusRef.current !== 'idle') handleRestart();
      }

      if (key === 'Escape') {
        handlePause();
        onClose();
      }
    };
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // All mutable values accessed via refs; stable actions (pause, resume, start, move*) are useCallback-stable

  // Tab switch → pause
  useEffect(() => {
    const handler = () => {
      if (!isOpenRef.current) return;
      if (statusRef.current === 'playing') { saveScore(); pause(); }
    };
    window.addEventListener('blur', handler);
    return () => window.removeEventListener('blur', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Click outside modal → pause + close
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === overlayRef.current) {
      handlePause();
      onClose();
    }
  };

  // ── Sizing ──────────────────────────────────────────────────────────────────

  const boardPx  = cellSize * W;
  const boardHPx = cellSize * H;
  const modalW   = boardPx + BOARD_GAP + SIDEBAR_W + 2 * PANEL_PAD;

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div
      className={styles.overlay}
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{ display: isOpen ? undefined : 'none' }}
    >
      <div className={styles.modal} style={{ width: modalW }}>

        {/* ── Board column + controls ───────────────────────────── */}
        <div className={styles.gameArea}>
          <div className={styles.boardColumn}>

            {/* Board */}
            <div className={styles.boardWrap} style={{ width: boardPx, height: boardHPx }}>
              <TetrisGrid cells={board} cols={W} rows={H} cellPx={cellSize} />

              {/* Overlay for idle / paused / gameover */}
              {status !== 'playing' && (
                <div className={styles.boardOverlay}>
                  {status === 'idle' && (
                    <button className={styles.startBtn} onClick={start}>
                      Jouer
                    </button>
                  )}

                  {status === 'paused' && (
                    <>
                      <div className={styles.overlayText}>PAUSE</div>
                      <div className={styles.overlayBtns}>
                        <button className={styles.startBtn} onClick={resume}>
                          Reprendre
                        </button>
                        <button className={styles.secondaryBtn} onClick={handleRestart}>
                          Rejouer
                        </button>
                      </div>
                    </>
                  )}

                  {status === 'gameover' && (
                    <>
                      <div className={styles.overlayText}>GAME OVER</div>
                      <button className={styles.startBtn} onClick={handleRestart}>
                        Rejouer
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ── Touch controls ───────────────────────────────────── */}
            <div className={styles.controlsArea}>

              {/* Left group: P / R (row 1), Space (row 2) */}
              <div className={styles.leftButtons}>
                <div className={styles.prRow}>
                  <button
                    className={styles.ctrlBtn}
                    onPointerDown={(e) => { e.preventDefault(); if (status === 'playing') { saveScore(); pause(); } else if (status === 'paused') resume(); }}
                    title="Pause / Reprendre (P)"
                  >
                    P
                  </button>
                  <button
                    className={styles.ctrlBtn}
                    onPointerDown={(e) => { e.preventDefault(); if (status !== 'idle') handleRestart(); }}
                    title="Rejouer (R)"
                  >
                    R
                  </button>
                </div>
                <button
                  className={styles.spaceBtn}
                  onPointerDown={(e) => { e.preventDefault(); hardDrop(); }}
                  title="Chute rapide (Espace)"
                >
                  Espace
                </button>
              </div>

              {/* Right group: directional pad */}
              <div className={styles.arrowGrid}>
                <div />{/* placeholder — col 1, row 1 */}
                <button
                  className={styles.ctrlBtn}
                  onPointerDown={(e) => { e.preventDefault(); rotate(); }}
                  title="Rotation (↑)"
                >
                  ↑
                </button>
                <div />{/* placeholder — col 3, row 1 */}
                <button
                  className={styles.ctrlBtn}
                  onPointerDown={(e) => { e.preventDefault(); moveLeft(); }}
                  title="Gauche (←)"
                >
                  ←
                </button>
                <button
                  className={styles.ctrlBtn}
                  onPointerDown={(e) => { e.preventDefault(); softDrop(); }}
                  title="Descente (↓)"
                >
                  ↓
                </button>
                <button
                  className={styles.ctrlBtn}
                  onPointerDown={(e) => { e.preventDefault(); moveRight(); }}
                  title="Droite (→)"
                >
                  →
                </button>
              </div>

            </div>
          </div>

          {/* ── Stats column ─────────────────────────────────────── */}
          <div className={styles.stats} style={{ width: SIDEBAR_W }}>
            <div className={styles.statBlock}>
              <div className={styles.statLabel}>Max Score</div>
              <ScoreDisplay value={maxScore} />
            </div>

            <div className={styles.statBlock}>
              <div className={styles.statLabel}>Score</div>
              <ScoreDisplay value={score} />
            </div>

            <div className={styles.statBlock}>
              <div className={styles.statLabel}>Lignes</div>
              <div className={styles.statValue}>{lines}</div>
            </div>

            <div className={styles.statBlock}>
              <div className={styles.statLabel}>Difficulté</div>
              <div className={styles.statValue}>{level}</div>
            </div>

            <div className={styles.statBlock}>
              <div className={styles.statLabel}>Prochain</div>
              <NextPiecePreview piece={next} />
            </div>

            <div className={styles.controls}>
              <div className={styles.controlsTitle}>Touches</div>
              <div className={styles.controlRow}><kbd>↑</kbd> Rotation</div>
              <div className={styles.controlRow}><kbd>← →</kbd> Déplacer</div>
              <div className={styles.controlRow}><kbd>↓</kbd> Descente</div>
              <div className={styles.controlRow}><kbd>Espace</kbd> Chute</div>
              <div className={styles.controlRow}><kbd>P</kbd> Pause</div>
              <div className={styles.controlRow}><kbd>R</kbd> Rejouer</div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
